<script>
  import { onMount, afterUpdate } from 'svelte';
  import { apiGet, apiUpdate, apiCreate, notify } from '../httpCommon';
  import Card, { Actions } from '@smui/card';
  import Paper, { Title, Content as PaperContent } from '@smui/paper';
  import { Textfield, Textshow, RichEdit } from '../components';
  import IconButton from '@smui/icon-button';
  import Button, { Label, Icon } from '@smui/button';
  import Select, { Option } from '@smui/select';
  import FormField from '@smui/form-field';
  import Switch from '@smui/switch';

  import Address from '../address/AddressEdit.svelte';
  import Contacts from '../users/Contacts.svelte';
  import Discounts from './VenueDiscounts.svelte';
  import { PopupList } from '../popups';

  export let id;
  export let title;

  const resource = 'venues';
  const basePath = `/${resource}`;
  const path = `/${resource}/${id}`;

  let data = { address: {}, collect_location: true, delivery_fee_active: true, delivery_fee_amount: 2 };

  let errors = {};

  $: isCreate = id === 'create';

  $: {
    if (isCreate) {
      title = 'Create Venue';
    } else if (data) {
      title = data.venue_name;
    } else {
      title = '';
    }
  }

  $: edit = edit || (errors && Object.keys(errors).length);

  function toggleEdit() {
    edit = !edit;
    console.log('edit: ', edit);
  }

  async function load() {
    try {
      data = await apiGet(basePath, id);
    } catch (error) {
      notify(error);
    }
  }

  async function save() {
    errors = {};
    try {
      if (isCreate) {
        data = await apiCreate(basePath, data);
        id = data.id;
      } else {
        data = await apiUpdate(basePath, id, data);
      }
      edit = false;
    } catch (error) {
      errors = error.errors;
    }
  }

  async function close() {
    if (isCreate) {
      navigate(basePath);
    } else if (id) {
      await load();
    }
    edit = false;
  }

  onMount(() => {
    if (!isCreate) {
      load();
    }
  });

  function toggle_delivery_fee() {
    data.delivery_fee_active = data.delivery_fee_active ? true : false;
    data.delivery_fee_amount = data.delivery_fee_active ? data.delivery_fee_amount : 0;
  }
</script>

<Paper elevation={5} class="top-level">
  <Title>
    <div class="title">
      <span>{title}</span>
      <span>
        {#if isCreate}
          <Button on:click={save}>
            <Icon class="material-icons">save</Icon>
            <Label>save</Label>
          </Button>
          <Button on:click={close}>
            <Icon class="material-icons">cancel</Icon>
            <Label>cancel</Label>
          </Button>
        {:else if edit}
          <Button on:click={load}>
            <Icon class="material-icons">replay</Icon>
            <Label>Reload</Label>
          </Button>
          <Button on:click={save}>
            <Icon class="material-icons">save</Icon>
            <Label>save</Label>
          </Button>
          <Button on:click={close}>
            <Icon class="material-icons">cancel</Icon>
            <Label>cancel</Label>
          </Button>
        {:else}
          <Button on:click={() => (edit = true)}>
            <Icon class="material-icons">settings_applications</Icon>
            <Label>Settings</Label>
          </Button>
        {/if}
      </span>
    </div>
  </Title>
  <PaperContent>
    <div class="content">
      {#if data}
        {#if edit || isCreate}
          <div class="card">
            <Card variant="outlined" padded>
              <h3>Venue</h3>
              <Textfield bind:value={data.venue_name} error={errors.venue_name} label="Venue Short Name" fullwidth />
              <Textfield bind:value={data.business_name} error={errors.business_name} label="Business Name" fullwidth />
              <Textfield bind:value={data.slug} error={errors.slug} label="Slug (URL name)" fullwidth />
              <Textfield bind:value={data.onsite_contact_number} error={errors.onsite_contact_number} label="Onsite customer service phone number" fullwidth />
              <p>
                <Select bind:value={data.venue_type} label="Venue type">
                  <Option value="COMMERCIAL">Commercial</Option>
                  <Option value="RESIDENTIAL">Residential</Option>
                </Select>
              </p>
              <p>
                <FormField>
                  <Switch value={true} bind:checked={data.collect_location} />
                  <span slot="label">Request customer location</span>
                </FormField>
              </p>
              <Textfield textarea fiullwidth bind:value={data.notes} label="Fooddisco Notes" />
              <Textfield textarea fiullwidth bind:value={data.pickup_notes} label="Pickup Notes" />

              <Textfield bind:value={data.market_size} error={errors.market_size} type="number" label="Total Tennants" />

              <Textfield bind:value={data.market_factor_pct} error={errors.market_factor_pct} type="number" label="Expected Market Reach %" />

              <Textshow label="Expected Sales" value={(data.market_size * data.market_factor_pct) / 100} />
            </Card>
          </div>
          <div class="card">
            <Address bind:data={data.address} errors={errors.address} />
            <Card variant="outlined" padded style="margin-top: 10px;">
              <h3>Delivery fee</h3>
              <FormField>
                <Switch bind:checked={data.delivery_fee_active} on:change={toggle_delivery_fee} />
                <span slot="label">{data.delivery_fee_active ? 'Delivery fee active' : 'Delivery fee inactive'}</span>
              </FormField>
              {#if data.delivery_fee_active}
                <FormField style="margin-top: 10px;">
                  <Textfield bind:value={data.delivery_fee_amount} label="Delivery fee amount (£)" input$minlength="1" input$maxlength="2" />
                </FormField>
              {/if}
            </Card>
          </div>
          <div class="card">
            <Card variant="outlined" padded>
              <h3>Site Info</h3>
              <Textfield class="stretch" bind:value={data.loading_location} error={errors.locading_location} label="Loading Location (lat,lon)" />
              <Textfield class="stretch" bind:value={data.loading_maps_link} error={errors.loading_maps_link} label="Loading maps link" />
              <Textfield class="stretch" bind:value={data.site_parking} error={errors.site_parking} label="Parking" />
              <RichEdit label="Entry Notes" bind:value={data.site_entry} />
              <Textfield class="stretch" bind:value={data.site_storage} error={errors.site_storage} label="Kiosk Storage Location" />
              <Textfield class="stretch" bind:value={data.site_location} error={errors.site_location} label="Kiosk Setup Location" />
              <Textfield class="stretch" bind:value={data.site_water} error={errors.site_water} label="Water" />
              <Textfield class="stretch" bind:value={data.site_power} error={errors.site_power} label="Power" />
              <Textfield class="stretch" bind:value={data.site_rubbish} error={errors.site_rubbish} label="Rubbish" />

              <RichEdit label="Site notes" bind:value={data.site_notes} />
            </Card>
          </div>
        {:else if !isCreate}
          <div class="card">
            <Contacts {path} />
          </div>
          <div class="card">
            <Discounts venueId={id} />
          </div>
          <div class="card wide">
            <PopupList venueId={id} title="Popups" topLevel={false} />
          </div>
        {/if}
      {/if}
    </div>
  </PaperContent>
</Paper>

<style>
  .title {
    display: flex;
    justify-content: space-between;
    text-transform: capitalize;
  }
  .content {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
</style>
